import React, { useMemo, useState } from "react";
import {
  filterRecords,
  competitionKPIs,
  distinctSchools,
} from "../../lib/aggregateAnalytics";
import {
  COMPETITION_META,
  REGISTRATION_COLLECTIONS,
  GRADE_OPTIONS,
  HOSTING_SCHOOLS,
} from "../../lib/registrationsConfig";
import RegistrationTimeline from "./RegistrationTimeline";

// Build competition options from config
const COMPETITION_OPTIONS = REGISTRATION_COLLECTIONS.flatMap((name) => {
  const meta = COMPETITION_META[name];
  if (!meta || meta.title === "Legacy") return [];
  return [{ collection: name, ...meta }];
});

function daysAgo(n) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - n + 1);
  return d;
}

const DATE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "All", days: null },
];

export default function CompetitionAnalytics({ records }) {
  const [selectedCollection, setSelectedCollection] = useState(
    COMPETITION_OPTIONS[0]?.collection ?? ""
  );

  // Five filters
  const [filterSchool, setFilterSchool] = useState("all");
  const [filterParticipation, setFilterParticipation] = useState("all");
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterHosting, setFilterHosting] = useState("all");
  const [datePreset, setDatePreset] = useState("All");

  const meta = COMPETITION_META[selectedCollection] ?? {};

  // Records for only the selected competition
  const compRecords = useMemo(
    () => records.filter((r) => r.collection === selectedCollection),
    [records, selectedCollection]
  );

  const dateRange = useMemo(() => {
    const found = DATE_PRESETS.find((p) => p.label === datePreset);
    if (!found || found.days === null) return { from: null, to: null };
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return { from: daysAgo(found.days), to: now };
  }, [datePreset]);

  const filtered = useMemo(
    () =>
      filterRecords(compRecords, {
        school: filterSchool,
        participationType: filterParticipation,
        grade: filterGrade,
        competitionSchool: filterHosting,
        from: dateRange.from,
        to: dateRange.to,
      }),
    [compRecords, filterSchool, filterParticipation, filterGrade, filterHosting, dateRange]
  );

  const kpis = useMemo(() => competitionKPIs(filtered), [filtered]);
  const schools = useMemo(() => distinctSchools(compRecords), [compRecords]);

  const resetFilters = () => {
    setFilterSchool("all");
    setFilterParticipation("all");
    setFilterGrade("all");
    setFilterHosting("all");
    setDatePreset("All");
  };

  const activeFilters = [
    filterSchool !== "all",
    filterParticipation !== "all",
    filterGrade !== "all",
    filterHosting !== "all",
    datePreset !== "All",
  ].filter(Boolean).length;

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3 className="analytics-card-title">Per-Competition Analytics</h3>
        {activeFilters > 0 && (
          <button className="reset-filters-btn" onClick={resetFilters}>
            Reset {activeFilters} filter{activeFilters > 1 ? "s" : ""}
          </button>
        )}
      </div>

      {/* Competition selector */}
      <div className="comp-selector-row">
        <label className="filter-label">Competition</label>
        <select
          className="analytics-select"
          value={selectedCollection}
          onChange={(e) => {
            setSelectedCollection(e.target.value);
            resetFilters();
          }}
        >
          {COMPETITION_OPTIONS.map((opt) => (
            <option key={opt.collection} value={opt.collection}>
              {opt.title} ({opt.competitionSchool})
            </option>
          ))}
        </select>
      </div>

      {/* Five filter row */}
      <div className="filters-row">
        {/* Filter 1: School */}
        <div className="filter-group">
          <label className="filter-label">School</label>
          <select
            className="analytics-select"
            value={filterSchool}
            onChange={(e) => setFilterSchool(e.target.value)}
          >
            <option value="all">All schools</option>
            {schools.map((s) => (
              <option key={s.schoolKey} value={s.schoolKey}>
                {s.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Filter 2: Participation type */}
        <div className="filter-group">
          <label className="filter-label">Participation</label>
          <select
            className="analytics-select"
            value={filterParticipation}
            onChange={(e) => setFilterParticipation(e.target.value)}
            disabled={!meta.hasParticipationType}
          >
            <option value="all">All</option>
            <option value="inter">Inter school</option>
            <option value="intra">Intra school</option>
          </select>
        </div>

        {/* Filter 3: Grade */}
        <div className="filter-group">
          <label className="filter-label">Grade</label>
          <select
            className="analytics-select"
            value={filterGrade}
            onChange={(e) => setFilterGrade(e.target.value)}
          >
            <option value="all">All grades</option>
            {GRADE_OPTIONS.map((g) => (
              <option key={g} value={g}>
                Grade {g}
              </option>
            ))}
          </select>
        </div>

        {/* Filter 4: Hosting school */}
        <div className="filter-group">
          <label className="filter-label">Pillar</label>
          <select
            className="analytics-select"
            value={filterHosting}
            onChange={(e) => setFilterHosting(e.target.value)}
          >
            <option value="all">All pillars</option>
            {HOSTING_SCHOOLS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Filter 5: Date preset */}
        <div className="filter-group">
          <label className="filter-label">Period</label>
          <div className="toggle-group">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                className={`toggle-btn ${datePreset === p.label ? "active" : ""}`}
                onClick={() => setDatePreset(p.label)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="kpi-row">
        <div className="kpi-card">
          <span className="kpi-value">{kpis.total}</span>
          <span className="kpi-label">Total</span>
        </div>
        {meta.hasParticipationType && (
          <>
            <div className="kpi-card">
              <span className="kpi-value">{kpis.interCount}</span>
              <span className="kpi-label">Inter</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value">{kpis.intraCount}</span>
              <span className="kpi-label">Intra</span>
            </div>
          </>
        )}
        <div className="kpi-card">
          <span className="kpi-value">{kpis.uniqueSchools}</span>
          <span className="kpi-label">Schools</span>
        </div>
      </div>

      {/* Mini time chart */}
      <RegistrationTimeline
        records={filtered}
        title="Filtered Timeline"
      />

      {/* School breakdown table */}
      {kpis.bySchool.length > 0 && (
        <div className="breakdown-tables">
          <div className="breakdown-table-wrap">
            <h4 className="breakdown-title">By School</h4>
            <table className="analytics-table compact">
              <thead>
                <tr>
                  <th>School</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {kpis.bySchool.map((row) => (
                  <tr key={row.displayName}>
                    <td>{row.displayName}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {Object.keys(kpis.byGrade).length > 0 && (
            <div className="breakdown-table-wrap">
              <h4 className="breakdown-title">By Grade</h4>
              <table className="analytics-table compact">
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {GRADE_OPTIONS.filter((g) => kpis.byGrade[g]).map((g) => (
                    <tr key={g}>
                      <td>Grade {g}</td>
                      <td>{kpis.byGrade[g]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
